import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  ArrowLeft,
  ArrowClockwise,
  ChartBar,
  CircleNotch,
  Coin,
  CurrencyDollar,
  Info,
  Cloud,
  Buildings,
  CaretDown,
  CaretRight,
  Warning,
} from "@phosphor-icons/react";
import { Tooltip, Collapse } from "antd";
import useAuth from "../../../hooks/useAuth";
import {
  getGlobalCostAnalysis,
  getIntakeChannelCost,
  getMatterCostAnalysis,
  CostAnalysisQueryError,
} from "../../../service/costAnalysis";
import {
  formatCurrency,
  formatCurrencyOrZero,
  formatCount,
  formatPercent,
  formatDate,
  ratio,
  costShare,
  barWidthPct,
} from "./utils";

/**
 * Cost Analysis Dashboard — super-admin-only Settings sub-page.
 *
 * Visualizes AI spending across the platform with a deliberate visual
 * separation between PER-MATTER LLM ACTIVITY (token spend attributable
 * to a specific matter) and FIRM-WIDE INFRASTRUCTURE OVERHEAD (RDS, EC2,
 * S3, vendor invoices). Both contribute to the "fully loaded cost" KPI,
 * but the partner's mental model treats them as distinct concepts and
 * the layout reflects that.
 *
 * Backend: `dms_backend/cost_tracking/` (PR #358 Phase 1 + PR #396 Phase 2).
 *
 * Pairs with `dms_backend` PR #396.
 *
 * Routing model:
 *   - This component is a Settings tab, so the URL stays `/settings`.
 *   - Matter-detail view is intra-component state (`selectedMatterId`),
 *     not a nested route. Deep-linking to a specific matter detail is
 *     a v2 nice-to-have.
 *
 * Auth model:
 *   - The `/settings` route is already gated by `isAdminOnly={true}` in
 *     AppRouter, so non-admins never reach this component. We add a
 *     defensive in-component check so an admin who later loses their
 *     role mid-session sees a friendly empty state rather than a 403
 *     soup of failed API calls. We DON'T redirect — admins occasionally
 *     impersonate other users to debug, and a hard redirect from a
 *     Settings sub-tab would be jarring.
 *
 * Data freshness:
 *   - Global endpoint can take 1-3s on 90-day windows. Skeleton shimmer
 *     covers the gap. Data updates either at LLM-call recording time
 *     (continuous, sub-cent) or on the daily 12:00 UTC cloud-billing
 *     ingestion job. We deliberately do NOT auto-poll — manual refresh
 *     button only.
 */

const WINDOW_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 60 days", days: 60 },
  { label: "Last 90 days", days: 90 },
];

const ATTRIBUTION_WARN_THRESHOLD = 0.95;
const PAGE_COST_TRACKING_START_DATE = "2026-07-15";

const CostAnalysis = () => {
  const { isAdmin } = useAuth();
  const [days, setDays] = useState(30);
  const [globalData, setGlobalData] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [globalError, setGlobalError] = useState(null);
  const [selectedMatterId, setSelectedMatterId] = useState(null);
  const [selectedMatterName, setSelectedMatterName] = useState(null);
  const [matterData, setMatterData] = useState(null);
  const [matterLoading, setMatterLoading] = useState(false);
  const [matterError, setMatterError] = useState(null);

  const fetchGlobal = useCallback(async () => {
    setGlobalLoading(true);
    setGlobalError(null);
    try {
      const res = await getGlobalCostAnalysis({ days });
      setGlobalData(res.data);
    } catch (err) {
      // Map error to a user-readable message.
      // - 403 → user lost admin privilege. Clear data AND surface a
      //   friendly message — the in-component isAdmin gate above will
      //   usually catch this on the next render, but if useAuth's role
      //   state hasn't refreshed yet we'd otherwise render a blank tab.
      // - 400 → backend rejected window params (shouldn't happen post
      //   client validation, but pin a friendly message just in case).
      // - Otherwise → generic.
      if (err?.response?.status === 403) {
        setGlobalData(null);
        setGlobalError(
          "Your account no longer has access to cost analysis. Refresh and try again, or ask an administrator to update your role."
        );
      } else if (err?.response?.status === 400) {
        setGlobalError(
          err.response?.data?.detail ||
            "Invalid window parameters. Try a different date range."
        );
      } else if (err instanceof CostAnalysisQueryError) {
        setGlobalError(err.message);
      } else {
        setGlobalError(
          err?.response?.data?.detail || "Failed to load cost analysis"
        );
      }
    } finally {
      setGlobalLoading(false);
    }
  }, [days]);

  const fetchMatter = useCallback(
    async (matterId) => {
      if (matterId == null) return;
      setMatterLoading(true);
      setMatterError(null);
      try {
        const res = await getMatterCostAnalysis(matterId, { days });
        setMatterData(res.data);
      } catch (err) {
        if (err?.response?.status === 404) {
          setMatterError("Matter not found, or no spend in this window.");
        } else if (err?.response?.status === 400) {
          setMatterError(
            err.response?.data?.detail ||
              "Invalid window parameters. Try a different date range."
          );
        } else {
          setMatterError(
            err?.response?.data?.detail || "Failed to load matter detail"
          );
        }
      } finally {
        setMatterLoading(false);
      }
    },
    [days]
  );

  useEffect(() => {
    if (!isAdmin) return;
    // Only refetch the global rollup when the global view is mounted.
    // Without this guard, a window-picker change while on matter detail
    // would fire BOTH fetchGlobal AND fetchMatter — the global call is
    // wasted (1–3s on a 90-day window), and Bugbot R2 caught it.
    if (selectedMatterId != null) return;
    fetchGlobal();
  }, [isAdmin, selectedMatterId, fetchGlobal]);

  useEffect(() => {
    if (!isAdmin) return;
    if (selectedMatterId != null) {
      fetchMatter(selectedMatterId);
    }
  }, [isAdmin, selectedMatterId, fetchMatter]);

  const openMatter = useCallback((matterId, matterName) => {
    setSelectedMatterId(matterId);
    setSelectedMatterName(matterName);
    setMatterData(null);
  }, []);

  const closeMatter = useCallback(() => {
    setSelectedMatterId(null);
    setSelectedMatterName(null);
    setMatterData(null);
    setMatterError(null);
  }, []);

  // Defensive in-component admin gate. Route layer should already block
  // non-admins, but this covers session-mid role changes and never
  // hard-redirects (admins occasionally impersonate users to debug).
  if (!isAdmin) {
    return (
      <Wrap>
        <EmptyState data-testid="cost-analysis-restricted">
          <Coin size={48} color="#CBD5E1" />
          <p>This page is restricted to administrators.</p>
          <p style={{ fontSize: 13, color: "#94A3B8" }}>
            If you believe you should have access, ask a system administrator
            to update your role.
          </p>
        </EmptyState>
      </Wrap>
    );
  }

  if (selectedMatterId != null) {
    return (
      <MatterDetailView
        matterId={selectedMatterId}
        matterName={selectedMatterName}
        data={matterData}
        loading={matterLoading}
        error={matterError}
        days={days}
        onChangeDays={setDays}
        onRefresh={() => fetchMatter(selectedMatterId)}
        onBack={closeMatter}
      />
    );
  }

  return (
    <GlobalView
      data={globalData}
      loading={globalLoading}
      error={globalError}
      days={days}
      onChangeDays={setDays}
      onRefresh={fetchGlobal}
      onOpenMatter={openMatter}
    />
  );
};

export default CostAnalysis;

// ---------------------------------------------------------------------------
// Global view
// ---------------------------------------------------------------------------

const GlobalView = ({
  data,
  loading,
  error,
  days,
  onChangeDays,
  onRefresh,
  onOpenMatter,
}) => {
  const [matterSearch, setMatterSearch] = useState("");
  const [mattersExpanded, setMattersExpanded] = useState(false);
  const byMatter = useMemo(() => data?.by_matter || [], [data]);
  const filteredMatters = useMemo(() => {
    const query = matterSearch.trim().toLowerCase();
    if (!query) return byMatter;
    return byMatter.filter((matter) =>
      [matter.matter_name, matter.client_name, matter.matter_id]
        .filter((value) => value != null)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [byMatter, matterSearch]);
  const matterDrilldownsSupported = byMatter.some((matter) =>
    Object.prototype.hasOwnProperty.call(
      matter,
      "pipeline_extraction_cost_usd"
    )
  );

  if (loading && !data) return <LoadingState label="Loading cost analysis…" />;
  if (error && !data)
    return <ErrorPanel error={error} onRetry={onRefresh} />;
  // If we have stale data and a refetch failed (window-picker change
  // during a flaky network), keep the stale data visible but surface
  // the failure inline above the chrome so the admin doesn't silently
  // read old numbers under a new window selection. This mirrors the
  // MatterDetailView pattern below (`{error && <ErrorPanel ... />}`).
  if (!data) return null;

  const summary = data.summary || {};
  const byModel = data.by_model || [];
  const byOperation = data.by_operation || [];
  const pipelineStagesSupported = Array.isArray(data.pipeline_by_stage);
  const pipelineByStage = pipelineStagesSupported
    ? data.pipeline_by_stage
    : [];
  const byDay = data.by_day || [];
  const infraByProvider = data.infra_by_provider || [];
  const infraByService = data.infra_by_service || [];
  const reconciliationSupported = Array.isArray(data.reconciliation);
  const reconciliation = reconciliationSupported ? data.reconciliation : [];
  const attributionRate =
    summary.attribution_rate != null
      ? parseFloat(summary.attribution_rate)
      : null;
  const attributionLow =
    attributionRate != null && attributionRate < ATTRIBUTION_WARN_THRESHOLD;

  return (
    <Wrap>
      <TopBar>
        <TopBarLeft>
          <CurrencyDollar size={22} weight="bold" color="#0C53BB" />
          <span>Cost Analysis</span>
          <WindowMeta>
            {formatDate(data.window_start)} – {formatDate(data.window_end)}
          </WindowMeta>
        </TopBarLeft>
        <TopBarRight>
          <WindowPicker days={days} onChange={onChangeDays} disabled={loading} />
          <RefreshBtn onClick={onRefresh} disabled={loading} aria-label="Refresh">
            <ArrowClockwise size={16} />
            Refresh
          </RefreshBtn>
        </TopBarRight>
      </TopBar>

      {/* Background-refresh failure: stale data visible above, but admin
          needs to know the latest window-picker change didn't actually land.
          Bugbot caught this on the original code (`error && !data` gated
          ErrorPanel out when stale data still existed). */}
      {error && (
        <RefreshErrorBanner data-testid="refresh-error-banner">
          <Warning size={18} weight="fill" />
          <span>
            Couldn&apos;t refresh — showing previous data.{" "}
            <strong>{error}</strong>
          </span>
          <RefreshBannerBtn onClick={onRefresh} disabled={loading}>
            <ArrowClockwise size={14} />
            Try again
          </RefreshBannerBtn>
        </RefreshErrorBanner>
      )}

      {attributionLow && (
        <AttributionBanner data-testid="attribution-low-banner">
          <Warning size={18} weight="fill" />
          <span>
            Only <strong>{formatPercent(attributionRate)}</strong> of LLM calls
            in this window are attributed to a matter. Check Sentry alerts for
            <code> cost_tracking.unattributed</code>.
          </span>
        </AttributionBanner>
      )}

      {/* ===== KPI strip — visually grouped per partner's "static overhead vs
          per-matter" separation. Two left cards are matter-attributable;
          two right cards are firm-wide pool. The vertical divider + group
          labels make the grouping explicit. ===== */}
      <KpiStrip>
        <KpiGroup>
          <KpiGroupLabel>Per-matter LLM activity</KpiGroupLabel>
          <KpiGroupCards>
            <KpiCard>
              <KpiCardLabel>Direct LLM spend</KpiCardLabel>
              <KpiCardValue>
                {formatCurrency(summary.llm_cost_usd)}
              </KpiCardValue>
              <KpiCardSub>
                {formatCount(summary.call_count)} calls ·{" "}
                {formatCount(summary.total_tokens)} tokens
              </KpiCardSub>
            </KpiCard>
            <KpiCard>
              <KpiCardLabel>Direct infrastructure</KpiCardLabel>
              <KpiCardValue>
                {formatCurrency(summary.direct_infra_cost_usd)}
              </KpiCardValue>
              <KpiCardSub>Cloud bills tagged to a matter</KpiCardSub>
            </KpiCard>
          </KpiGroupCards>
        </KpiGroup>
        <KpiGroup>
          <KpiGroupLabel>
            Firm-wide infrastructure
            <Tooltip
              title="Cloud bills (RDS, EC2, S3, vendor invoices) that aren't
                     attributable to a specific matter. Allocated across matters
                     by token share when computing fully loaded cost."
            >
              <InfoIconWrap>
                <Info size={14} />
              </InfoIconWrap>
            </Tooltip>
          </KpiGroupLabel>
          <KpiGroupCards>
            <KpiCard>
              <KpiCardLabel>Shared infrastructure pool</KpiCardLabel>
              <KpiCardValue>
                {formatCurrency(summary.shared_infra_cost_usd)}
              </KpiCardValue>
              <KpiCardSub>
                {formatCount(summary.infra_record_count)} billing records
              </KpiCardSub>
            </KpiCard>
            <KpiCard $hero>
              <KpiCardLabel>
                Fully loaded cost
                <Tooltip title="Total LLM + direct infra + allocated firm overhead.">
                  <InfoIconWrap>
                    <Info size={12} />
                  </InfoIconWrap>
                </Tooltip>
              </KpiCardLabel>
              <KpiCardValue $hero>
                {formatCurrency(summary.fully_loaded_cost_usd)}
              </KpiCardValue>
              <KpiCardSub>What this period really cost the firm</KpiCardSub>
            </KpiCard>
          </KpiGroupCards>
        </KpiGroup>
      </KpiStrip>

      {/* ===== Section 1: Per-matter LLM activity ===== */}
      <SectionHeader>
        <ChartBar size={20} />
        <span>Per-matter LLM activity</span>
        <SectionHeaderHint>
          Token spend attributable to a specific matter, plus that matter&apos;s
          allocated share of firm overhead.
        </SectionHeaderHint>
      </SectionHeader>

      <Section>
        <SectionTitle>
          <SectionToggle
            type="button"
            onClick={() => setMattersExpanded((expanded) => !expanded)}
            aria-expanded={mattersExpanded}
            aria-controls="cost-matters-section"
          >
            {mattersExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
            Matters by total cost
          </SectionToggle>
          <SectionTitleSub>
            {filteredMatters.length} of {byMatter.length} matters in window
          </SectionTitleSub>
        </SectionTitle>
        {mattersExpanded && (
          <MatterSectionBody id="cost-matters-section">
            <MatterSearchInput
              type="search"
              value={matterSearch}
              onChange={(event) => setMatterSearch(event.target.value)}
              placeholder="Search client, matter, or matter ID"
              aria-label="Search client matters"
            />
            {byMatter.length === 0 ? (
              <InlineEmpty>No matters had LLM activity in this window.</InlineEmpty>
            ) : filteredMatters.length === 0 ? (
              <InlineEmpty>No client matters match this search.</InlineEmpty>
            ) : (
              <TableScroll>
            <MatterTable aria-label="Matters by total cost">
              <thead>
                <tr>
                  <th>Matter</th>
                  <th>Client</th>
                  <th className="num">Tokens</th>
                  <th className="num">Calls</th>
                  {matterDrilldownsSupported ? (
                    <>
                      <th className="num">Extraction</th>
                      <th className="num">Categorization</th>
                      <th className="num">Graphing</th>
                      <th className="num">Embedding</th>
                      <th className="num">PI Case Review</th>
                    </>
                  ) : (
                    <th className="num">DMS Pipeline</th>
                  )}
                  <th className="num">The Brief</th>
                  <th className="num">Chat</th>
                  <th className="num">LLM cost</th>
                  <th className="num">Direct infra</th>
                  <th className="num">
                    <Tooltip title="This matter's share of firm-wide infrastructure, allocated by token share. Negative values reflect promotional credits that exceeded shared spend in this window.">
                      <span style={{ borderBottom: "1px dotted #94A3B8" }}>
                        Allocated overhead
                      </span>
                    </Tooltip>
                  </th>
                  <th className="num">Total cost</th>
                </tr>
              </thead>
              <tbody>
                {filteredMatters.map((m) => {
                  const isUnattributed = m.matter_id == null;
                  return (
                    <tr
                      key={m.matter_id ?? "unattributed"}
                      onClick={
                        isUnattributed
                          ? undefined
                          : () => onOpenMatter(m.matter_id, m.matter_name)
                      }
                      className={isUnattributed ? "" : "clickable"}
                    >
                      <td className="name">
                        {isUnattributed ? (
                          <em style={{ color: "#94A3B8" }}>(unattributed)</em>
                        ) : (
                          m.matter_name || `Matter ${m.matter_id}`
                        )}
                      </td>
                      <td className="name">
                        {isUnattributed ? (
                          <em style={{ color: "#94A3B8" }}>—</em>
                        ) : (
                          m.client_name || "—"
                        )}
                      </td>
                      <td className="num">{formatCount(m.total_tokens)}</td>
                      <td className="num">{formatCount(m.call_count)}</td>
                      {matterDrilldownsSupported ? (
                        <>
                          <td className="num product">
                            {formatCurrencyOrZero(
                              m.pipeline_extraction_cost_usd
                            )}
                          </td>
                          <td className="num product">
                            {formatCurrencyOrZero(
                              m.pipeline_categorization_cost_usd
                            )}
                          </td>
                          <td className="num product">
                            {formatCurrencyOrZero(
                              m.pipeline_graphing_cost_usd
                            )}
                          </td>
                          <td className="num product">
                            {formatCurrencyOrZero(
                              m.pipeline_embedding_cost_usd
                            )}
                          </td>
                          <td className="num product pi">
                            {formatCurrencyOrZero(
                              m.pi_case_review_cost_usd
                            )}
                          </td>
                        </>
                      ) : (
                        <td className="num product">
                          {formatCurrencyOrZero(m.pipeline_cost_usd)}
                        </td>
                      )}
                      <td className="num product">
                        {formatCurrencyOrZero(m.brief_cost_usd)}
                      </td>
                      <td className="num product">
                        {formatCurrencyOrZero(m.chat_cost_usd)}
                      </td>
                      <td className="num">{formatCurrency(m.llm_cost_usd)}</td>
                      <td className="num">
                        {formatCurrency(m.direct_infra_cost_usd)}
                      </td>
                      <td className="num subtle">
                        {formatCurrency(m.allocated_overhead_usd)}
                      </td>
                      <td className="num strong">
                        {formatCurrency(m.total_cost_usd)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </MatterTable>
              </TableScroll>
            )}
          </MatterSectionBody>
        )}
      </Section>

      <Section data-testid="global-pipeline-stage-section">
        <SectionTitle>
          <span>DMS Pipeline cost by stage</span>
          <SectionTitleSub>
            Product-facing pipeline stages; raw operation names remain below.
          </SectionTitleSub>
        </SectionTitle>
        <PipelineStageTable
          rows={pipelineByStage}
          pipelineCostUsd={summary.pipeline_cost_usd}
          supported={pipelineStagesSupported}
        />
      </Section>

      <SectionRow>
        <Section style={{ flex: 1 }}>
          <SectionTitle>By model</SectionTitle>
          <BarTable rows={byModel} keyField="model_name" valueField="total_cost_usd" />
        </Section>
        <Section style={{ flex: 1 }}>
          <SectionTitle>By operation</SectionTitle>
          <BarTable
            rows={byOperation}
            keyField="operation_type"
            valueField="total_cost_usd"
            labelFormatter={formatOperationLabel}
          />
        </Section>
      </SectionRow>

      <Section>
        <SectionTitle>Daily LLM spend</SectionTitle>
        <DailyTrend rows={byDay} />
      </Section>

      <Section data-testid="intake-channel-cost-section">
        <SectionTitle>
          <span>Intake channel cost</span>
          <SectionTitleSub>
            Attributable LLM spend for documents received through Mail and Fax.
          </SectionTitleSub>
        </SectionTitle>
        <SectionRow>
          <IntakeChannelCostCard channel="mail" title="Mail" />
          <IntakeChannelCostCard channel="fax" title="Fax" />
        </SectionRow>
      </Section>

      <Section data-testid="cost-reconciliation-section">
        <SectionTitle>
          <span>Modeled versus actual cost</span>
          <SectionTitleSub>
            DMS product spend is reconciled daily; WHG Hermes stays separate.
          </SectionTitleSub>
        </SectionTitle>
        <ReconciliationTable
          rows={reconciliation}
          supported={reconciliationSupported}
        />
      </Section>

      {/* ===== Section 2: Firm-wide infrastructure overhead ===== */}
      <SectionHeader $variant="overhead">
        <Buildings size={20} />
        <span>Firm-wide infrastructure overhead</span>
        <SectionHeaderHint>
          {reconciliationSupported
            ? "Infrastructure bills (RDS, EC2, S3, compute) that aren't tied to a specific matter. Vendor LLM invoices appear in reconciliation above."
            : "Cloud bills that aren't tied to a specific matter. Allocated across matters by token share."}
        </SectionHeaderHint>
      </SectionHeader>

      <SectionRow>
        <Section style={{ flex: 1 }}>
          <SectionTitle>
            <Cloud size={16} />
            <span>By provider</span>
          </SectionTitle>
          <InfraTable rows={infraByProvider} columns={[
            { key: "provider", label: "Provider", className: "name" },
            { key: "cost_usd", label: "Cost", className: "num strong", format: formatCurrency },
            { key: "record_count", label: "Records", className: "num", format: formatCount },
          ]} />
        </Section>
        <Section style={{ flex: 1 }}>
          <SectionTitle>
            <Buildings size={16} />
            <span>By service</span>
          </SectionTitle>
          <InfraTable rows={infraByService} columns={[
            { key: "service", label: "Service", className: "name" },
            { key: "provider", label: "Provider", className: "name", subtle: true },
            { key: "cost_usd", label: "Cost", className: "num strong", format: formatCurrency },
            { key: "record_count", label: "Records", className: "num", format: formatCount },
          ]} />
        </Section>
      </SectionRow>
    </Wrap>
  );
};

const isoDateOffset = (offsetDays) => {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
};

const shiftIsoDate = (value, offsetDays) => {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
};

const formatCentsPerPage = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? `${(amount * 100).toFixed(2)}¢` : "—";
};

const pageCostDefaultEnd = () => {
  const today = isoDateOffset(0);
  return today < PAGE_COST_TRACKING_START_DATE
    ? PAGE_COST_TRACKING_START_DATE
    : today;
};

const IntakeChannelCostCard = ({ channel, title }) => {
  const [start, setStart] = useState(PAGE_COST_TRACKING_START_DATE);
  const [end, setEnd] = useState(pageCostDefaultEnd);
  const [appliedRange, setAppliedRange] = useState(() => ({
    start: PAGE_COST_TRACKING_START_DATE,
    end: pageCostDefaultEnd(),
  }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterError, setFilterError] = useState("");

  const fetchCost = useCallback(async () => {
    setLoading(true);
    setError("");
    setData(null);
    try {
      const response = await getIntakeChannelCost(channel, {
        start: appliedRange.start,
        // The API's end boundary is exclusive; the visible picker is inclusive.
        end: shiftIsoDate(appliedRange.end, 1),
      });
      setData(response.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail || `Failed to load ${title} cost.`
      );
    } finally {
      setLoading(false);
    }
  }, [appliedRange, channel, title]);

  useEffect(() => {
    fetchCost();
  }, [fetchCost]);

  const applyRange = () => {
    if (!start || !end || start > end) {
      setError("");
      setFilterError("Choose a valid date range.");
      return;
    }
    if (start < PAGE_COST_TRACKING_START_DATE) {
      setError("");
      setFilterError("Page-cost tracking began on July 15, 2026.");
      return;
    }
    setFilterError("");
    if (start === appliedRange.start && end === appliedRange.end) {
      fetchCost();
      return;
    }
    setAppliedRange({ start, end });
  };

  return (
    <ChannelCostCard data-testid={`${channel}-cost-card`}>
      <ChannelCostHeader>
        <strong>{title}</strong>
        <span>LLM dollars</span>
      </ChannelCostHeader>
      <ChannelCostValue data-testid={`${channel}-cost-total`}>
        {error
          ? "Unavailable"
          : loading && !data
            ? "Loading…"
            : data
              ? formatCurrencyOrZero(data.total_cost_usd)
              : "Unavailable"}
      </ChannelCostValue>
      <ChannelPageCost data-testid={`${channel}-page-cost`}>
        {error
          ? "Page cost unavailable"
          : loading && !data
            ? "Loading page counts…"
            : data?.cost_per_page_usd != null
          ? `${formatCount(data.page_count)} pages · ${formatCentsPerPage(
              data.cost_per_page_usd
            )}/page · ${formatPercent(data.page_coverage_rate)} coverage`
              : data?.document_count > 0
                ? `${formatCount(
                    data.documents_with_page_count
                  )} of ${formatCount(
                    data.document_count
                  )} documents have page counts`
                : "Page counts are being collected"}
      </ChannelPageCost>
      <ChannelDateRow>
        <label>
          From
          <input
            type="date"
            aria-label={`${title} start date`}
            value={start}
            min={PAGE_COST_TRACKING_START_DATE}
            onChange={(event) => setStart(event.target.value)}
          />
        </label>
        <label>
          Through
          <input
            type="date"
            aria-label={`${title} end date`}
            value={end}
            onChange={(event) => setEnd(event.target.value)}
          />
        </label>
        <ChannelApplyButton onClick={applyRange} disabled={loading}>
          Apply
        </ChannelApplyButton>
      </ChannelDateRow>
      {(error || filterError) && (
        <ChannelError role="alert">{error || filterError}</ChannelError>
      )}
    </ChannelCostCard>
  );
};

// ---------------------------------------------------------------------------
// Matter detail view
// ---------------------------------------------------------------------------

const MatterDetailView = ({
  matterId,
  matterName,
  data,
  loading,
  error,
  days,
  onChangeDays,
  onRefresh,
  onBack,
}) => {
  const summary = data?.summary || {};
  const allocation = data?.allocation || {};
  const totalDisplay = parseFloat(summary.fully_loaded_cost_usd || 0);

  // Compute % share for the breakdown card. Floats over Decimal strings
  // are fine for display ratios.
  const llmShare = ratio(summary.llm_cost_usd, totalDisplay);
  const directShare = ratio(summary.direct_infra_cost_usd, totalDisplay);
  const allocatedShare = ratio(summary.allocated_overhead_usd, totalDisplay);
  const tokenShare = parseFloat(allocation.token_share || 0);
  const productCost = (key) => summary[key] ?? allocation[key] ?? 0;
  const pipelineStagesSupported = Array.isArray(data?.pipeline_by_stage);
  const pipelineByStage = pipelineStagesSupported
    ? data.pipeline_by_stage
    : [];

  return (
    <Wrap>
      <TopBar>
        <TopBarLeft>
          <BackBtn onClick={onBack}>
            <ArrowLeft size={16} />
            Back to overview
          </BackBtn>
          <BreadcrumbSep>·</BreadcrumbSep>
          <span style={{ fontWeight: 600 }}>
            {matterName || `Matter ${matterId}`}
          </span>
          {data?.window_start && (
            <WindowMeta>
              {formatDate(data.window_start)} – {formatDate(data.window_end)}
            </WindowMeta>
          )}
        </TopBarLeft>
        <TopBarRight>
          <WindowPicker days={days} onChange={onChangeDays} disabled={loading} />
          <RefreshBtn onClick={onRefresh} disabled={loading} aria-label="Refresh">
            <ArrowClockwise size={16} />
            Refresh
          </RefreshBtn>
        </TopBarRight>
      </TopBar>

      {loading && !data && <LoadingState label="Loading matter detail…" />}
      {/* Hard-fail with no data on screen: render the heavy centered
          ErrorPanel so the user has nowhere to go but retry. With stale
          data already painted, drop down to the subtle inline
          RefreshErrorBanner instead — same pattern GlobalView uses, so
          the two views stay visually consistent (Bugbot R3 catch). */}
      {error && !data && <ErrorPanel error={error} onRetry={onRefresh} />}
      {error && data && (
        <RefreshErrorBanner data-testid="matter-refresh-error-banner">
          <Warning size={18} weight="fill" />
          <span>
            Couldn&apos;t refresh — showing previous data.{" "}
            <strong>{error}</strong>
          </span>
          <RefreshBannerBtn onClick={onRefresh} disabled={loading}>
            <ArrowClockwise size={14} />
            Try again
          </RefreshBannerBtn>
        </RefreshErrorBanner>
      )}

      {data && (
        <>
          <KpiStrip>
            <KpiGroup>
              <KpiGroupLabel>This matter (per-matter)</KpiGroupLabel>
              <KpiGroupCards>
                <KpiCard>
                  <KpiCardLabel>LLM cost</KpiCardLabel>
                  <KpiCardValue>
                    {formatCurrency(summary.llm_cost_usd)}
                  </KpiCardValue>
                  <KpiCardSub>
                    {formatCount(summary.call_count)} calls ·{" "}
                    {formatCount(summary.total_tokens)} tokens
                  </KpiCardSub>
                </KpiCard>
                <KpiCard>
                  <KpiCardLabel>Direct infrastructure</KpiCardLabel>
                  <KpiCardValue>
                    {formatCurrency(summary.direct_infra_cost_usd)}
                  </KpiCardValue>
                  <KpiCardSub>Cloud spend tagged to this matter</KpiCardSub>
                </KpiCard>
              </KpiGroupCards>
            </KpiGroup>
            <KpiGroup>
              <KpiGroupLabel>Allocated firm overhead</KpiGroupLabel>
              <KpiGroupCards>
                <KpiCard>
                  <KpiCardLabel>Allocated share</KpiCardLabel>
                  <KpiCardValue>
                    {formatCurrency(summary.allocated_overhead_usd)}
                  </KpiCardValue>
                  <KpiCardSub>
                    {formatPercent(tokenShare)} of firm tokens
                  </KpiCardSub>
                </KpiCard>
                <KpiCard $hero>
                  <KpiCardLabel>Fully loaded cost</KpiCardLabel>
                  <KpiCardValue $hero>
                    {formatCurrency(summary.fully_loaded_cost_usd)}
                  </KpiCardValue>
                  <KpiCardSub>LLM + direct + allocated</KpiCardSub>
                </KpiCard>
              </KpiGroupCards>
            </KpiGroup>
          </KpiStrip>

          <Section data-testid="matter-product-cost-section">
            <SectionTitle>
              <span>AI product cost summary</span>
              <SectionTitleSub>
                Direct AI spend grouped by the product attorneys use.
              </SectionTitleSub>
            </SectionTitle>
            <ProductCostGrid>
              <KpiCard>
                <KpiCardLabel>DMS Pipeline</KpiCardLabel>
                <KpiCardValue data-testid="matter-pipeline-cost">
                  {formatCurrencyOrZero(productCost("pipeline_cost_usd"))}
                </KpiCardValue>
                <KpiCardSub>Document processing and enrichment</KpiCardSub>
              </KpiCard>
              <KpiCard>
                <KpiCardLabel>The Brief</KpiCardLabel>
                <KpiCardValue data-testid="matter-brief-cost">
                  {formatCurrencyOrZero(productCost("brief_cost_usd"))}
                </KpiCardValue>
                <KpiCardSub>Attorney analysis and briefing</KpiCardSub>
              </KpiCard>
              <KpiCard>
                <KpiCardLabel>Chat</KpiCardLabel>
                <KpiCardValue data-testid="matter-chat-cost">
                  {formatCurrencyOrZero(productCost("chat_cost_usd"))}
                </KpiCardValue>
                <KpiCardSub>Matter-scoped AI conversations</KpiCardSub>
              </KpiCard>
              <KpiCard>
                <KpiCardLabel>PI Case Review</KpiCardLabel>
                <KpiCardValue data-testid="matter-pi-case-review-cost">
                  {formatCurrencyOrZero(
                    productCost("pi_case_review_cost_usd")
                  )}
                </KpiCardValue>
                <KpiCardSub>
                  PI categorization, graphing, and case-review work product
                </KpiCardSub>
              </KpiCard>
            </ProductCostGrid>
          </Section>

          <Section data-testid="matter-pipeline-stage-section">
            <SectionTitle>
              <span>DMS Pipeline cost by stage</span>
              <SectionTitleSub>
                Pipeline stages for this matter; raw operations remain below.
              </SectionTitleSub>
            </SectionTitle>
            <PipelineStageTable
              rows={pipelineByStage}
              pipelineCostUsd={productCost("pipeline_cost_usd")}
              supported={pipelineStagesSupported}
            />
          </Section>

          <Section>
            <Collapse
              ghost
              items={[
                {
                  key: "calc",
                  label: (
                    <CalcLabel>
                      <Info size={16} /> How is this calculated?
                    </CalcLabel>
                  ),
                  children: (
                    <CalcBody>
                      <CalcRow>
                        <span>LLM token spend (direct):</span>
                        <strong>{formatCurrency(summary.llm_cost_usd)}</strong>
                        <span className="pct">{formatPercent(llmShare)}</span>
                      </CalcRow>
                      <CalcRow>
                        <span>Direct infrastructure (matter-tagged):</span>
                        <strong>
                          {formatCurrency(summary.direct_infra_cost_usd)}
                        </strong>
                        <span className="pct">
                          {formatPercent(directShare)}
                        </span>
                      </CalcRow>
                      <CalcRow>
                        <span>
                          Allocated share of firm-wide infrastructure
                          {allocation.token_share != null && (
                            <small>
                              {" "}
                              (this matter&apos;s {formatPercent(tokenShare)}{" "}
                              share of the firm&apos;s overall LLM tokens in
                              this window)
                            </small>
                          )}
                          :
                        </span>
                        <strong>
                          {formatCurrency(summary.allocated_overhead_usd)}
                        </strong>
                        <span className="pct">
                          {formatPercent(allocatedShare)}
                        </span>
                      </CalcRow>
                      <CalcDivider />
                      <CalcRow $total>
                        <span>Fully loaded cost:</span>
                        <strong>
                          {formatCurrency(summary.fully_loaded_cost_usd)}
                        </strong>
                        <span className="pct">100.0%</span>
                      </CalcRow>
                      {allocation.shared_pool_total_usd != null && (
                        <CalcFootnote>
                          Firm-wide shared pool this window:{" "}
                          <strong>
                            {formatCurrency(allocation.shared_pool_total_usd)}
                          </strong>
                          . Allocation method:{" "}
                          {allocation.method || "token-share"}.
                        </CalcFootnote>
                      )}
                    </CalcBody>
                  ),
                },
              ]}
            />
          </Section>

          <SectionRow>
            <Section style={{ flex: 1 }}>
              <SectionTitle>By model</SectionTitle>
              <BarTable
                rows={data.by_model || []}
                keyField="model_name"
                valueField="total_cost_usd"
              />
            </Section>
            <Section style={{ flex: 1 }}>
              <SectionTitle>By operation</SectionTitle>
              <BarTable
                rows={data.by_operation || []}
                keyField="operation_type"
                valueField="total_cost_usd"
              />
            </Section>
          </SectionRow>

          <Section>
            <SectionTitle>Daily LLM spend</SectionTitle>
            <DailyTrend rows={data.by_day || []} />
          </Section>

          <SectionRow>
            <Section style={{ flex: 1 }}>
              <SectionTitle>
                <Cloud size={16} />
                <span>Infrastructure by provider</span>
              </SectionTitle>
              <InfraTable
                rows={data.infra_by_provider || []}
                columns={[
                  { key: "provider", label: "Provider", className: "name" },
                  {
                    key: "direct_cost_usd",
                    label: "Direct",
                    className: "num",
                    format: formatCurrency,
                  },
                  {
                    key: "allocated_cost_usd",
                    label: "Allocated",
                    className: "num subtle",
                    format: formatCurrency,
                  },
                  {
                    key: "cost_usd",
                    label: "Total",
                    className: "num strong",
                    format: formatCurrency,
                  },
                ]}
              />
            </Section>
            <Section style={{ flex: 1 }}>
              <SectionTitle>
                <Buildings size={16} />
                <span>Infrastructure by service</span>
              </SectionTitle>
              <InfraTable
                rows={data.infra_by_service || []}
                columns={[
                  { key: "service", label: "Service", className: "name" },
                  {
                    key: "provider",
                    label: "Provider",
                    className: "name",
                    subtle: true,
                  },
                  {
                    key: "direct_cost_usd",
                    label: "Direct",
                    className: "num",
                    format: formatCurrency,
                  },
                  {
                    key: "allocated_cost_usd",
                    label: "Allocated",
                    className: "num subtle",
                    format: formatCurrency,
                  },
                  {
                    key: "cost_usd",
                    label: "Total",
                    className: "num strong",
                    format: formatCurrency,
                  },
                ]}
              />
            </Section>
          </SectionRow>
        </>
      )}
    </Wrap>
  );
};

// ---------------------------------------------------------------------------
// Reusable sub-components
// ---------------------------------------------------------------------------

const WindowPicker = ({ days, onChange, disabled }) => (
  <PickerWrap>
    <label htmlFor="cost-analysis-window">Window:</label>
    <select
      id="cost-analysis-window"
      value={days}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {WINDOW_PRESETS.map((p) => (
        <option key={p.days} value={p.days}>
          {p.label}
        </option>
      ))}
    </select>
  </PickerWrap>
);

const PipelineStageTable = ({ rows, pipelineCostUsd, supported }) => {
  if (!rows?.length) {
    return (
      <StageEmpty data-testid="pipeline-stage-empty">
        {supported
          ? "No DMS Pipeline stage activity occurred in this window."
          : "Stage-level pipeline costs are not available from this backend yet."}
        {!supported && (
          <span>The rest of Cost Analysis remains available.</span>
        )}
      </StageEmpty>
    );
  }

  return (
    <TableScroll>
      <StageTable aria-label="DMS Pipeline cost by stage">
        <thead>
          <tr>
            <th>Stage</th>
            <th className="num">Calls</th>
            <th className="num">Tokens</th>
            <th className="num">Cost</th>
            <th className="num">% of pipeline cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.stage || row.operation_type || "stage"}-${index}`}>
              <td className="name">
                {row.stage_label || row.stage || row.operation_type || "Unknown stage"}
              </td>
              <td className="num">{formatCount(row.call_count)}</td>
              <td className="num">{formatCount(row.total_tokens)}</td>
              <td className="num strong">
                {formatCurrencyOrZero(row.total_cost_usd)}
              </td>
              <td className="num">
                {formatPercent(
                  costShare(row.total_cost_usd, pipelineCostUsd)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </StageTable>
    </TableScroll>
  );
};

const RECONCILIATION_STATUS = {
  matched: { label: "Matched", tone: "good" },
  variance: { label: "Variance", tone: "warn" },
  missing_usage: { label: "Missing usage", tone: "bad" },
  missing_invoice: { label: "Missing invoice", tone: "bad" },
  no_activity: { label: "No activity", tone: "neutral" },
  actual_only: { label: "Actual only", tone: "info" },
};

const ReconciliationTable = ({ rows, supported }) => {
  if (!supported) {
    return (
      <StageEmpty>
        Daily reconciliation is not available from this backend yet.
      </StageEmpty>
    );
  }
  if (!rows?.length) {
    return (
      <StageEmpty>
        No completed reconciliation days yet.
        <span>The first full UTC day after the cutover will appear here.</span>
      </StageEmpty>
    );
  }

  return (
    <TableScroll>
      <ReconciliationTableStyled aria-label="Modeled versus actual cost">
        <thead>
          <tr>
            <th>Day</th>
            <th>Provider</th>
            <th>Scope</th>
            <th className="num">Modeled</th>
            <th className="num">Actual</th>
            <th className="num">Variance</th>
            <th className="num">Variance %</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const status = RECONCILIATION_STATUS[row.status] || {
              label: row.status || "Unknown",
              tone: "neutral",
            };
            const scopeLabel =
              row.scope === "dms"
                ? "DMS Chat + VA Briefs"
                : row.scope === "hermes"
                  ? "WHG Hermes"
                  : row.scope || "Other";
            const providerLabel = row.provider
              ? `${row.provider.charAt(0).toUpperCase()}${row.provider.slice(1)}`
              : "Unknown";
            return (
              <tr key={`${row.provider}-${row.scope}-${row.period_start}`}>
                <td>{formatDate(row.period_start)}</td>
                <td className="name">{providerLabel}</td>
                <td className="name">{scopeLabel}</td>
                <td className="num">{formatCurrencyOrZero(row.modeled_cost_usd)}</td>
                <td className="num">{formatCurrencyOrZero(row.actual_cost_usd)}</td>
                <td className="num">{formatCurrencyOrZero(row.variance_usd)}</td>
                <td className="num">
                  {row.variance_pct == null
                    ? "—"
                    : formatPercent(parseFloat(row.variance_pct))}
                </td>
                <td>
                  <ReconciliationStatus $tone={status.tone}>
                    {status.label}
                  </ReconciliationStatus>
                </td>
              </tr>
            );
          })}
        </tbody>
      </ReconciliationTableStyled>
    </TableScroll>
  );
};

const PI_OPERATION_LABELS = {
  pi_categorization: "PI categorization",
  pi_graph_extraction: "PI graph extraction",
  pi_case_review: "PI Case Review",
};

const formatOperationLabel = (operation) =>
  PI_OPERATION_LABELS[operation] || operation;

const BarTable = ({
  rows,
  keyField,
  valueField,
  labelFormatter = (value) => value,
}) => {
  const max = useMemo(() => {
    if (!rows?.length) return 0;
    return rows.reduce((acc, r) => {
      const v = parseFloat(r[valueField] || 0);
      return Math.max(acc, Math.abs(v));
    }, 0);
  }, [rows, valueField]);

  if (!rows?.length) {
    return <InlineEmpty>No data in this window.</InlineEmpty>;
  }

  return (
    <BarRows>
      {rows.map((r, idx) => {
        const value = r[valueField];
        const width = barWidthPct(value, max);
        return (
          <BarRow key={`${r[keyField] || "row"}-${idx}`}>
            <BarLabel>
              <span className="name">
                {labelFormatter(r[keyField]) || "—"}
              </span>
              {r.provider && r.provider !== r[keyField] && (
                <span className="meta">{r.provider}</span>
              )}
            </BarLabel>
            <BarTrack>
              <BarFill style={{ width: `${width}%` }} />
            </BarTrack>
            <BarValue>{formatCurrency(value)}</BarValue>
          </BarRow>
        );
      })}
    </BarRows>
  );
};

const InfraTable = ({ rows, columns }) => {
  if (!rows?.length) {
    return <InlineEmpty>No data in this window.</InlineEmpty>;
  }
  return (
    <SimpleTable>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} className={c.className}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx}>
            {columns.map((c) => {
              const v = row[c.key];
              const display = c.format ? c.format(v) : v ?? "—";
              return (
                <td
                  key={c.key}
                  className={`${c.className || ""}${c.subtle ? " subtle" : ""}`}
                >
                  {display}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </SimpleTable>
  );
};

const DailyTrend = ({ rows }) => {
  if (!rows?.length) return <InlineEmpty>No daily data.</InlineEmpty>;
  // Use absolute value for the bar height (negative-cost days are rare
  // — credits — and we still want them visible in the trendline).
  const max = rows.reduce(
    (acc, r) => Math.max(acc, Math.abs(parseFloat(r.total_cost_usd || 0))),
    0
  );
  const total = rows.reduce(
    (acc, r) => acc + parseFloat(r.total_cost_usd || 0),
    0
  );
  return (
    <DailyTrendWrap>
      <DailyTrendBars role="img" aria-label="Daily LLM spend trend">
        {rows.map((r, idx) => {
          const v = parseFloat(r.total_cost_usd || 0);
          const h = barWidthPct(Math.abs(v), max);
          return (
            <Tooltip
              key={r.day || idx}
              title={`${formatDate(r.day)}: ${formatCurrency(r.total_cost_usd)}`}
            >
              <DailyTrendBar
                style={{ height: `${Math.max(2, h)}%` }}
                $negative={v < 0}
              />
            </Tooltip>
          );
        })}
      </DailyTrendBars>
      <DailyTrendFooter>
        <span>
          {formatDate(rows[0]?.day)} → {formatDate(rows[rows.length - 1]?.day)}
        </span>
        <span>
          Total: <strong>{formatCurrency(total)}</strong>
        </span>
      </DailyTrendFooter>
    </DailyTrendWrap>
  );
};

const LoadingState = ({ label }) => (
  <Wrap>
    <Loading>
      <CircleNotch size={28} className="spin" />
      <span>{label}</span>
    </Loading>
    <SkeletonGrid>
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </SkeletonGrid>
    <SkeletonBlock />
    <SkeletonBlock />
  </Wrap>
);

const ErrorPanel = ({ error, onRetry }) => (
  <ErrorWrap data-testid="cost-analysis-error">
    <Warning size={28} color="#EF4444" />
    <span>{error}</span>
    {onRetry && (
      <RefreshBtn onClick={onRetry}>
        <ArrowClockwise size={16} />
        Try again
      </RefreshBtn>
    )}
  </ErrorWrap>
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  overflow-y: auto;
  padding-bottom: 40px;
  font-family: Manrope, sans-serif;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  .spin {
    animation: spin 1s linear infinite;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const WindowMeta = styled.span`
  font-weight: 400;
  font-size: 13px;
  color: #64748b;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 13px;
  font-weight: 500;
  color: #0c53bb;
  cursor: pointer;
  font-family: "Manrope", sans-serif;
  &:hover {
    background: #f1f6fe;
  }
`;

const BreadcrumbSep = styled.span`
  color: #cbd5e1;
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  font-family: "Manrope", sans-serif;
  &:hover:not(:disabled) {
    background: #e2e8f0;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PickerWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #475569;
  label {
    font-weight: 500;
  }
  select {
    padding: 5px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-family: "Manrope", sans-serif;
    font-size: 13px;
    background: #fff;
    cursor: pointer;
  }
`;

const AttributionBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  color: #78350f;
  font-size: 13px;
  code {
    background: rgba(120, 53, 15, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
`;

const RefreshErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  color: #991b1b;
  font-size: 13px;
  span {
    flex: 1;
    min-width: 0;
  }
`;

const RefreshBannerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #fca5a5;
  background: #fff;
  color: #991b1b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #fef2f2;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const KpiStrip = styled.div`
  display: flex;
  gap: 14px;
  align-items: stretch;
  flex-wrap: wrap;
`;

const KpiGroup = styled.div`
  flex: 1 1 360px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const KpiGroupLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const KpiGroupCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${(p) => (p.$hero ? "#F1F6FE" : "#F8FAFC")};
  border: 1px solid ${(p) => (p.$hero ? "#C3D5F1" : "#F1F5F9")};
`;

const KpiCardLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const KpiCardValue = styled.div`
  font-size: ${(p) => (p.$hero ? "22px" : "20px")};
  font-weight: 700;
  color: ${(p) => (p.$hero ? "#0C53BB" : "#0F172A")};
  line-height: 1.2;
`;

const KpiCardSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

const InfoIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  color: #94a3b8;
  cursor: help;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding-bottom: 6px;
  border-bottom: 2px solid
    ${(p) => (p.$variant === "overhead" ? "#FED7AA" : "#C3D5F1")};
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  flex-wrap: wrap;
  > svg {
    color: ${(p) => (p.$variant === "overhead" ? "#EA580C" : "#0C53BB")};
  }
`;

const SectionHeaderHint = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
  margin-left: 8px;
`;

const SectionRow = styled.div`
  display: flex;
  gap: 14px;
  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const Section = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  > svg {
    color: #64748b;
  }
`;

const SectionToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  cursor: pointer;
`;

const MatterSectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionTitleSub = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: #94a3b8;
`;

const InlineEmpty = styled.div`
  font-size: 13px;
  color: #94a3b8;
  padding: 10px 0;
`;

const MatterSearchInput = styled.input`
  width: min(420px, 100%);
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 8px 10px;
  color: #334155;
  background: #fff;
  outline: none;
  &:focus {
    border-color: #0c53bb;
    box-shadow: 0 0 0 2px rgba(12, 83, 187, 0.1);
  }
`;

const ChannelCostCard = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 14px;
  background: #f8fafc;
`;

const ChannelCostHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  color: #334155;
  span {
    color: #94a3b8;
    font-size: 11px;
  }
`;

const ChannelCostValue = styled.div`
  color: #0c53bb;
  font-size: 24px;
  font-weight: 700;
  margin: 8px 0 12px;
  font-variant-numeric: tabular-nums;
`;

const ChannelPageCost = styled.div`
  color: #64748b;
  font-size: 12px;
  margin: -6px 0 12px;
  font-variant-numeric: tabular-nums;
`;

const ChannelDateRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  flex-wrap: wrap;
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: #64748b;
    font-size: 11px;
  }
  input {
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 6px 8px;
    color: #334155;
    background: #fff;
  }
`;

const ChannelApplyButton = styled.button`
  border: 1px solid #0c53bb;
  border-radius: 6px;
  padding: 6px 12px;
  background: #fff;
  color: #0c53bb;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ChannelError = styled.div`
  margin-top: 8px;
  color: #b91c1c;
  font-size: 12px;
`;

const StageEmpty = styled(InlineEmpty)`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 16px 0 10px;
  color: #64748b;
  span {
    color: #94a3b8;
    font-size: 12px;
  }
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const MatterTable = styled.table`
  width: 100%;
  min-width: 1420px;
  border-collapse: collapse;
  font-size: 13px;
  th {
    text-align: left;
    padding: 8px 10px;
    color: #64748b;
    font-weight: 500;
    border-bottom: 1px solid #f1f5f9;
    font-size: 12px;
    white-space: nowrap;
  }
  th.num,
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  td {
    padding: 9px 10px;
    border-bottom: 1px solid #f8fafc;
    color: #334155;
  }
  td.name {
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  td.subtle {
    color: #64748b;
    font-style: italic;
  }
  td.product {
    color: #0c53bb;
    font-weight: 600;
  }
  td.strong {
    font-weight: 700;
    color: #0f172a;
  }
  tr.clickable {
    cursor: pointer;
  }
  tr.clickable:hover {
    background: #f1f6fe;
  }
`;

const ProductCostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const SimpleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th {
    text-align: left;
    padding: 8px 10px;
    color: #64748b;
    font-weight: 500;
    border-bottom: 1px solid #f1f5f9;
    font-size: 12px;
  }
  th.num,
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  td {
    padding: 8px 10px;
    border-bottom: 1px solid #f8fafc;
    color: #334155;
  }
  td.subtle {
    color: #94a3b8;
  }
  td.strong {
    font-weight: 600;
    color: #0f172a;
  }
`;

const StageTable = styled(SimpleTable)`
  min-width: 640px;
  td.name {
    color: #1e293b;
    font-weight: 600;
  }
`;

const ReconciliationTableStyled = styled(SimpleTable)`
  min-width: 760px;
  td.name {
    font-weight: 600;
  }
`;

const ReconciliationStatus = styled.span`
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $tone }) =>
    $tone === "good"
      ? "#166534"
      : $tone === "warn"
        ? "#92400e"
        : $tone === "bad"
          ? "#991b1b"
          : $tone === "info"
            ? "#1d4ed8"
            : "#475569"};
  background: ${({ $tone }) =>
    $tone === "good"
      ? "#dcfce7"
      : $tone === "warn"
        ? "#fef3c7"
        : $tone === "bad"
          ? "#fee2e2"
          : $tone === "info"
            ? "#dbeafe"
            : "#f1f5f9"};
`;

const BarRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 200px) 1fr 90px;
  align-items: center;
  gap: 10px;
  font-size: 13px;
`;

const BarLabel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  .name {
    color: #1e293b;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    color: #94a3b8;
    font-size: 11px;
  }
`;

const BarTrack = styled.div`
  background: #f1f5f9;
  border-radius: 4px;
  height: 10px;
  overflow: hidden;
`;

const BarFill = styled.div`
  background: linear-gradient(90deg, #0c53bb, #06b6d4);
  height: 100%;
  border-radius: 4px;
  transition: width 0.2s ease;
`;

const BarValue = styled.div`
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #334155;
  font-weight: 500;
`;

const DailyTrendWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DailyTrendBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 80px;
  padding: 4px 0;
  border-bottom: 1px solid #f1f5f9;
`;

const DailyTrendBar = styled.div`
  flex: 1;
  min-width: 4px;
  background: ${(p) => (p.$negative ? "#94A3B8" : "#0C53BB")};
  border-radius: 2px 2px 0 0;
  transition: opacity 0.15s ease;
  cursor: pointer;
  &:hover {
    opacity: 0.75;
  }
`;

const DailyTrendFooter = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #64748b;
  strong {
    color: #0f172a;
  }
`;

const CalcLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #0c53bb;
`;

const CalcBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
`;

const CalcRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 60px;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
  color: ${(p) => (p.$total ? "#0F172A" : "#475569")};
  font-weight: ${(p) => (p.$total ? 700 : 400)};
  small {
    color: #94a3b8;
    font-weight: 400;
  }
  strong {
    font-variant-numeric: tabular-nums;
  }
  .pct {
    text-align: right;
    color: #94a3b8;
    font-variant-numeric: tabular-nums;
    font-size: 12px;
  }
`;

const CalcDivider = styled.hr`
  border: none;
  border-top: 1px solid #e2e8f0;
  margin: 4px 0;
`;

const CalcFootnote = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #f1f5f9;
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  padding: 30px 0;
  color: #64748b;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
`;

const SkeletonCard = styled.div`
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 10px;
  height: 86px;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonBlock = styled(SkeletonCard)`
  height: 180px;
`;

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 50px 0;
  color: #64748b;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 60px 0;
  color: #94a3b8;
  text-align: center;
  p {
    margin: 0;
  }
`;
